import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification } from '@telus-uds/components-web';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportExcel } from '../utils/apiService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  formatNumber,
  formatPercentage,
  getDefaultDateRange
} from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import FilterPanel from '../components/FilterPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ScopedDataNotice from '../components/ScopedDataNotice';
import DrillDownModal from '../components/DrillDownModal';
import { chartCardStyle } from '../utils/chartStyles';

const EmailActivityReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [filterValues, setFilterValues] = useState({
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
    type: '',
    status: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(null);

  const fetchData = async (customFilters = null) => {
    setLoading(true);
    setError(null);
    try {
      const filters = customFilters || filterValues;
      setAppliedFilters(filters);
      const result = await reportsAPI.getEmailActivity(filters);
      setData(result.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch email activity data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultDateRange().startDate,
      endDate: getDefaultDateRange().endDate,
      type: '',
      status: ''
    };
    setFilterValues(defaultFilters);
    // Fetch data immediately with default filters
    fetchData(defaultFilters);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportFilters = appliedFilters || filterValues;
      const fileName = `email-activity-report-${exportFilters.startDate}-to-${exportFilters.endDate}.xlsx`;
      await exportReportExcel('/reports/email-activity/export/excel', exportFilters, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const emailTypes = [
    { value: 'ORDER_NOTIFICATION', label: 'Order Notification' },
    { value: 'IOT_ORDER_NOTIFICATION', label: 'IoT Order Notification' },
    { value: 'BA_ORDER_NOTIFICATION', label: 'BA Order Notification' },
    { value: 'CUSTOMER_NOTIFICATION', label: 'Customer Notification' },
    { value: 'ORDER_QUOTE_APPROVAL', label: 'Order Quote Approval' },
    { value: 'ORDER_STATUS_CHANGE_BY_SOV', label: 'Order Status Change by SOV' },
    { value: 'DIRECT_ROUTING_ORDER', label: 'Direct Routing Order' },
    { value: 'CALL_CENTRE_ORDER', label: 'Call Centre Order' }
  ];

  const filters = [
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      value: filterValues.startDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      value: filterValues.endDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'type',
      label: 'Email Type',
      type: 'select',
      value: filterValues.type,
      options: [{ value: '', label: 'All' }, ...emailTypes]
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filterValues.status,
      options: [
        { value: '', label: 'All' },
        { value: 'SUCCESS', label: 'Success' },
        { value: 'FAILED', label: 'Failed' }
      ]
    }
  ];

  if (loading) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <LoadingSpinner text="Loading Email Activity Report..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const { summary, by_type, status, hourly_distribution, daily_distribution } = data;

  // Calculate success rate
  const successItem = status && status.find(s => s.status === 'SUCCESS');
  const failedItem = status && status.find(s => s.status === 'FAILED');
  const successCount = parseInt((successItem && successItem.count) || 0, 10);
  const failedCount = parseInt((failedItem && failedItem.count) || 0, 10);
  const totalEmails = (summary && summary.total_emails_sent) || 0;
  const successRate = totalEmails > 0 ? ((successCount / totalEmails) * 100).toFixed(2) : 0;

  // Prepare chart data
  const emailTypeData = by_type && by_type.slice(0, 10).map(item => ({
    name: (item.type || '').replace(/_/g, ' '),
    rawValue: item.type,
    count: parseInt(item.count, 10)
  })) || [];

  const statusData = status && status.map(item => ({
    name: item.status || 'Unknown',
    value: parseInt(item.count, 10)
  })) || [];

  const hourlyData = hourly_distribution && hourly_distribution.map(item => ({
    hour: `${item.hour}:00`,
    count: parseInt(item.count, 10)
  })) || [];

  const dailyData = daily_distribution && daily_distribution.map(item => ({
    date: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A',
    count: parseInt(item.count, 10)
  })) || [];

  return (
    <div>
      {error && (
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      )}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onExport={handleExportExcel}
        isExporting={isExporting}
      />
      <ScopedDataNotice />

      {/* Summary Cards */}
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Total Emails Sent"
              value={formatNumber(totalEmails)}
              icon="📧"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Success Rate"
              value={`${successRate}%`}
              color={successRate >= 95 ? '#00A600' : '#FF9900'}
              badge={{
                text: `${formatNumber(successCount)} successful`,
                background: '#e8f5e9',
                color: '#00A600'
              }}
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Failed Count"
              value={formatNumber(failedCount)}
              color={failedCount > 0 ? '#CC0000' : '#00A600'}
              icon={failedCount > 0 ? '⚠️' : '✅'}
            />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Charts Section */}
      <FlexGrid style={{ marginTop: '2rem' }}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Email Types (Top 10)
              </Typography>
              <p style={{ fontSize: '0.75rem', color: '#999999', marginBottom: '0.5rem' }}>Click a bar to view the underlying emails</p>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={emailTypeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={200} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="count"
                    fill="#0066CC"
                    name="Email Count"
                    onClick={entry => setDrillDownType(entry && entry.rawValue)}
                    style={{ cursor: 'pointer' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Success vs Failed
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.name === 'SUCCESS' ? '#00A600' : '#CC0000'}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, entry) => {
                      const percentage = ((value / totalEmails) * 100).toFixed(2);
                      return [`${formatNumber(value)} (${percentage}%)`, entry.payload.name];
                    }}
                  />
                  <Legend 
                    formatter={(value, entry) => {
                      const itemValue = entry.payload.value;
                      const percentage = ((itemValue / totalEmails) * 100).toFixed(2);
                      return `${value}: ${formatNumber(itemValue)} (${percentage}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row>
          <FlexGrid.Col xs={12} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Hourly Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#0066CC"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        {dailyData.length > 1 && (
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  Daily Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#4b286d"
                      fill="#4b286d"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        )}
      </FlexGrid>

      <DrillDownModal
        open={!!drillDownType}
        onClose={() => setDrillDownType(null)}
        title={`Emails — ${drillDownType}`}
        queryParams={{
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
          type: drillDownType,
          status: filterValues.status
        }}
        fetchRecords={reportsAPI.getEmailActivityRecords}
        columns={[
          { key: 'to', label: 'To' },
          { key: 'subject', label: 'Subject' },
          { key: 'type', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'created_at', label: 'Created At' }
        ]}
      />
    </div>
  );
};

export default EmailActivityReport;
